import { useCallback, useEffect, useRef } from "react";

import { Select } from "antd";

import { SavedFilterModel } from "models";
import { EntityKind } from "models/entityKind";

import { BaseDropdown } from "components/base";

const { Option } = Select;

interface SavedFilterSelectionProps {
  className?: string;
  savedFilters: SavedFilterModel[];
  selectedFilter: SavedFilterModel | null;
  onSelectedFilterChanged: (filter: SavedFilterModel) => void;
  useSelectControl?: boolean;
  entityKind: EntityKind;
  isModified?: boolean;
}

function SavedFilterSelection({
  className = "",
  savedFilters,
  selectedFilter,
  onSelectedFilterChanged,
  useSelectControl = false,
  entityKind,
  isModified = false,
  ...props
}: SavedFilterSelectionProps) {
  const savedFiltersRef = useRef<SavedFilterModel[]>([]);

  useEffect(() => {
    savedFiltersRef.current = savedFilters;
  }, [savedFilters, entityKind]);

  const handleFilterChange = useCallback(
    (value) => {
      const match = savedFilters.find((f) => f.id === value);

      if (match) {
        onSelectedFilterChanged && onSelectedFilterChanged(match);
      }
    },
    [savedFilters, onSelectedFilterChanged]
  );

  const isLoading = selectedFilter?.name === "Loading...";

  // Custom template for rendering the selected filter with italic style when modified
  const renderSelectedFilter = useCallback(
    (filter) => {
      if (!filter) return null;
      const filterName = filter.name || "";
      return (
        <span
          style={{
            fontStyle: isModified ? "italic" : "normal"
          }}>
          {filterName}
        </span>
      );
    },
    [isModified]
  );

  // The select control does not adjust the popover well based in the number
  // of items.  Therefore in some case we need to still use BaseDropdown
  if (useSelectControl) {
    return (
      <Select
        {...props}
        className={`${className} ${isModified ? "filter-modified" : ""}`}
        loading={isLoading}
        value={selectedFilter?.id}
        onChange={handleFilterChange}>
        {savedFilters.map((filter) => {
          return (
            <Option key={filter.id}>
              <span
                style={{
                  fontStyle:
                    isModified && filter.id === selectedFilter?.id ? "italic" : "normal"
                }}>
                {filter.name}
              </span>
            </Option>
          );
        })}
      </Select>
    );
  }
  return (
    <BaseDropdown
      className={`${className} ${isModified ? "filter-modified" : ""}`}
      value={selectedFilter}
      isLoading={isLoading}
      testid={`saved-filter-selection-${entityKind}`}
      labelKey={"name"}
      selectedTemplate={renderSelectedFilter}
      onChange={(val) => {
        onSelectedFilterChanged && onSelectedFilterChanged(val);
      }}
      options={savedFilters}
    />
  );
}

export default SavedFilterSelection;
